import React, {CSSProperties, useEffect} from 'react';
import {Modal as MantineModal, Button, Group, Box, MantineNumberSize, Text} from '@mantine/core';
import styles from './StandardModal.module.scss';
import {AppTheme} from "../../../appTheme";
import {Alert} from "react-bootstrap";

export type StandardModalProps = {
  opened: boolean;
  title: React.ReactNode;
  onCancel: () => void;
  onSubmit?: () => void;
  submitText?: string;
  cancelText?: string;
  submitDisabled?: boolean;
  loading?: boolean;
  size?: MantineNumberSize;
  withinPortal?: boolean;
  withCloseButton?: boolean;
  /** Control visibility of the green submit button in the footer. Default: true */
  showSubmit?: boolean;
  bodyStyles?: CSSProperties;
  formId?: string; // if provided, Submit button will submit the target form
  /** When true, disables Mantine Modal body scroll area so content can control its own height */
  noBodyScroll?: boolean;
  /** Control modal focus trap; when false, avoids auto-focusing first input on open */
  trapFocus?: boolean;
  /** Specify which element receives initial focus when modal opens */
  initialFocus?: number | string | HTMLElement | null;
  errorMessage?: string;
  children: React.ReactNode;
};

const StandardModal: React.FC<StandardModalProps> = ({
  opened,
  title,
  onCancel,
  onSubmit,
  submitText = 'Submit',
  cancelText = 'Cancel',
  submitDisabled,
  loading,
  size = 'lg',
  withinPortal = true,
  withCloseButton = false,
  showSubmit = true,
  formId,
  bodyStyles,
  noBodyScroll,
  trapFocus,
  initialFocus,
  errorMessage,
  children
}) => {

  return (
    <MantineModal
      opened={opened}
      onClose={onCancel}
      centered
      size={size}
      trapFocus={trapFocus}
      title={
        typeof title === 'string'
          ? <h1 style={{ color: AppTheme.colors.themeGreen }}>{title}</h1>
          : title
      }
      withinPortal={withinPortal}
      withCloseButton={withCloseButton}
      styles={{
        // Ensure header doesn't reserve space for a close button and can stretch fully
        header: { width: '100%', paddingRight: withCloseButton ? undefined : 0, alignItems: 'center' },
        // Make the title container flex so custom headers can span full width
        // Also remove Mantine's default right margin that reserves space for the close button
        // flex: 1 ensures the title block consumes the row, letting inner children push to edges
        title: { width: '100%',  marginRight: withCloseButton ? undefined : 0},
      }}
      // Always disable Mantine internal ScrollArea to avoid nested scrollbars.
      // We'll manage scrolling within our own body container so footer can remain visible.
      scrollAreaComponent={!noBodyScroll ? Box : undefined}
    >
      <Box className={styles['container']}>
          {errorMessage && (
            <Alert variant={"danger"}>
              {errorMessage}
            </Alert>
          )}
          {children}
        <Group position="right" className={styles['footer']}>
        <Button type="button" color="red" onClick={onCancel} disabled={!!loading}>
          {cancelText}
        </Button>
        {showSubmit && (
          formId ? (
            <Button type="submit" color="green" form={formId} disabled={!!loading || !!submitDisabled}>
              {submitText}
            </Button>
          ) : (
            <Button type="button" color="green" onClick={onSubmit} disabled={!!loading || !!submitDisabled}>
              {submitText}
            </Button>
          )
        )}
        </Group>
      </Box>
    </MantineModal>
  );
};

export default StandardModal;
